/**
 * Axios instance with the platform's auth and error conventions.
 *
 * Token handling follows the JWT-opacity contract: the token is stored and forwarded
 * **verbatim**, never re-signed or mutated, and `jwt-decode` is used only to read `exp` for
 * client-side refresh scheduling — never for an authorization decision.
 */

import axios, { AxiosError, type AxiosInstance } from 'axios';

export const TOKEN_KEY = 'jwt_token';
/** Where the admin's pre-impersonation token is stashed (platform convention). */
export const IMPERSONATION_ORIGINAL_TOKEN_KEY = 'impersonation_original_token';
export const BEDROCK_URL_KEY = 'bedrock_server_url';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export const api: AxiosInstance = axios.create({
  baseURL: '/',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** Set by AuthContext so the interceptor can trigger a logout without importing React state. */
let onUnauthenticated: (() => void) | null = null;
let onForbiddenApp: ((message: string) => void) | null = null;

export function registerAuthHandlers(handlers: {
  onUnauthenticated: () => void;
  onForbiddenApp: (message: string) => void;
}): void {
  onUnauthenticated = handlers.onUnauthenticated;
  onForbiddenApp = handlers.onForbiddenApp;
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ detail?: unknown }>) => {
    const status = error.response?.status;
    const detail = extractDetail(error);

    if (status === 401) {
      // Session expired or invalid — re-authenticate. The backend has already collapsed every
      // /auth/verify user-state reason into this one bucket.
      clearToken();
      onUnauthenticated?.();
    } else if (status === 403 && /not authorized to access this app/i.test(detail)) {
      // The `app_not_allowed` signal: a friendly screen, NOT a silent bounce to /login.
      onForbiddenApp?.(detail);
    }

    return Promise.reject(error);
  },
);

/** Pull a human-readable message out of the platform's `{"detail": ...}` error shape. */
export function extractDetail(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      // 422 validation errors: [{loc, msg, type}, ...]
      return detail
        .map((item) => (typeof item?.msg === 'string' ? item.msg : JSON.stringify(item)))
        .join('; ');
    }
    // A gateway or proxy error has no `detail` — the request never reached our API, so say so
    // rather than surfacing a bare "Request failed with status code 502", which tells the
    // operator nothing about where to look.
    const status = error.response?.status;
    if (status === 502 || status === 504) {
      return (
        `The API did not respond (${status}). It may be restarting or unreachable behind the ` +
        'proxy — check `docker compose ps` and the servicedesk-api logs.'
      );
    }
    if (status === 503) {
      return `A required service is unavailable (${status}). Check /health/ready.`;
    }
    if (error.message) return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

/**
 * Seconds to wait after a 429, from the `Retry-After` header.
 *
 * Bedrock rate-limits `/auth/login`, `/auth/mfa/complete` and `/auth/sso/{id}/callback`; the
 * contract requires honouring the header rather than firing a tight retry loop that compounds
 * the limit.
 */
export function retryAfterSeconds(error: unknown, fallback = 5): number {
  if (axios.isAxiosError(error)) {
    const header = error.response?.headers?.['retry-after'];
    const parsed = Number(header);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return fallback;
}

export function isRateLimited(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 429;
}
